import { Validate } from '@oneethos/shared'
import { useState } from 'react'
import { useEffect } from 'react'
import { EligibilityFields } from './eligibility'
import { FormInput, Validation } from '.'
import { AgreementCheckbox } from '../../components/agreement-checkbox'
import { SmsConsentDisclosure } from '../../components/sms-consent-disclosure'
import { useAppState } from '../../hooks'

const { validateFields, ValidatorMap } = Validate

/**
 * This component overlaps heavily with the CoborrowerPrequal component, but there are 
 * enough differences that it's likely best to keep them separate. Regardless, changes
 * here should prompt consideration of changes to the CoborrowerPrequal component.
 */
export const PrimaryBorrowerPrequal = ({
  formData,
  schema,
  update,
  submitPrequal,
  autoSubmitPrequal,
  installer
}) => {
  const [validation, setValidation] = useState<Record<string, string>>({})
  const { tenant } = useAppState()
  const usesPersona = formData.identityVerificationVersion === 'persona' && !tenant.config?.personaDisabled

  const validate = () => {
    const results = validateFields(schema, formData, [
      'firstName',
      'lastName',
      'phone',
      'email',
      'userAddress',
      'birthDate',
    ])


    if (formData.installedAtDifferentLocation === 'yes') {
      const err = ValidatorMap.address(formData.userAddress)
      if (err) {
        results.userAddress = 'Installation address must be complete'
      }
    }

    if (['no-hit', 'reject'].includes(formData.prequalResult) && Validate.isValidSocial(formData.ssn) !== '') {
      results.ssn = 'Social security number is required to verify your credit eligibility'
    }

    return results
  }

  useEffect(() => {
    // if the form is valid and the user is logged in, it's likely a user
    // returning from the registration process, attempt to submit the prequal;
    // autoSubmitPrequal only runs once per Eligibility step so a rejected submit
    // doesn't resubmit when this component remounts
    const v = validate()
    if (
      !formData.prequalError &&
      !formData.prequalResult &&
      Object.keys(v).length === 0 &&
      installer && !installer.isSupport
    ) {
      autoSubmitPrequal()
    }
  }, [])


  const needsMoreInfo = ['no-hit', 'reject'].includes(formData.prequalResult)

  return <>
    <h2>Eligibility</h2>
    <p>
      Get started quickly to know if you're eligible. This will run a soft credit inquiry
      and will not affect your credit.
    </p>
    <EligibilityFields formData={formData} update={update} />
    {needsMoreInfo || formData.ssn ? <div className="form-group">
      {needsMoreInfo ? <div className="alert alert-danger">
        <div className="mb-2">We couldn't find enough information to verify your credit eligibility.</div>
        <div>
          Please review the information above for accuracy. Please make sure to use
          your full legal name and address. Please also include your social security number to
          ensure access to your credit profile.
        </div>
      </div> : null}
      <FormInput
        field='ssn'
        value={formData.ssn || ''}
        onChange={s => update('ssn', s)}
      />
    </div> : null}
    { !usesPersona ? null : <SmsConsentDisclosure actionLabel="Check Eligibility" />}
    <AgreementCheckbox
      formData={formData}
      update={update}
      name="agreesToProceedWithPrequal"
    >
      By selecting "Check Eligibility" I am providing written instructions to {tenant.config?.name} under the federal
      Fair Credit Reporting Act and applicable state law, authorizing {tenant.config?.name} to obtain information
      about me from consumer reporting agencies to determine if I am eligible for a loan. I understand
      that {tenant.config?.name} will access my credit profile using a soft credit inquiry, which will not
      impact my credit score.
    </AgreementCheckbox>

    <div className="buttons">
      <button className="btn btn-primary"
        type="button"
        disabled={!formData.agreesToProceedWithPrequal?.agrees}
        onClick={() => {
          const v = validate()
          setValidation(v)
          if (Object.keys(v).length === 0) {
            submitPrequal()
          }
        }}>
        Check Eligibility
      </button>
      {process.env.REACT_APP_NODE_ENV === 'test' ? <div>
        {/* TODO: note that testing w/o a prequal report will NOT trigger adverse action */}
        <div className="alert alert-secondary mt-3">
          Test results will not trigger adverse action
          because a prequal record is required.
          <div className="buttons mt-3">
            {['pass', 'fail', 'no-hit', 'reject'].map((test) => <button type="button" key={test}
              className="btn btn-secondary"
              onClick={() => {
                const v = validate()
                setValidation(v)
                if (Object.keys(v).length === 0) {
                  submitPrequal(test as 'pass' | 'fail' | 'no-hit' | 'reject')
                }
              }}
            >Check Eligibility ({test})</button>)}
          </div>
        </div>
      </div> : null}
    </div>
    {validation ? <Validation validation={validation} /> : null}
  </>
}

export default PrimaryBorrowerPrequal